import { cn } from "@/lib/utils";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import { TrendBadge } from "./TrendBadge";
import { ReactNode } from "react";

interface KPICardProps {
  title: string;
  value: string | number;
  icon: ReactNode;
  trend?: number;
  previousValue?: string | number;
  loading?: boolean;
  subtitle?: string;
  detail?: string;
}

export function KPICard({
  title,
  value,
  icon,
  trend,
  previousValue,
  loading = false,
  subtitle,
  detail,
}: KPICardProps) {
  const getTrendIcon = () => {
    if (!trend) return Minus;
    return trend > 0 ? TrendingUp : TrendingDown;
  };

  const TrendIcon = getTrendIcon();
  const isPositive = trend !== undefined && trend > 0;
  const isNegative = trend !== undefined && trend < 0;

  return (
    <Card className="relative h-[156px] overflow-hidden rounded-2xl border-border/70 shadow-sm transition-shadow hover:shadow-md sm:h-[160px]">
      {/* Gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent" />
      
      <CardHeader className="relative space-y-0 p-3 pb-2 sm:p-4 sm:pb-2">
        <div className="min-w-0 flex-1 flex flex-col">
          <p className="min-h-8 text-xs font-medium leading-4 text-muted-foreground sm:text-sm">
            {title}
          </p>
          {subtitle && (
            <p className="text-[10px] leading-tight text-muted-foreground/70 sm:text-xs" title={subtitle}>
              {subtitle}
            </p>
          )}
        </div>
      </CardHeader>
      
      <CardContent className="relative px-3 pb-3 pt-0 sm:px-4">
        {loading ? (
          <div className="space-y-2">
            <div className="h-10 w-32 bg-muted animate-pulse rounded" />
            <div className="h-4 w-24 bg-muted animate-pulse rounded" />
          </div>
        ) : (
          <>
            <div className="flex min-w-0 items-center justify-between gap-1">
              <div className="min-w-0 break-words text-[28px] font-semibold leading-none tabular-nums sm:text-3xl">
                {value}
              </div>
              {trend !== undefined && trend !== 0 && (
                <TrendBadge 
                  value={trend} 
                  isPositive={isPositive}
                  showIcon 
                />
              )}
              <div className="shrink-0 rounded-lg bg-primary/10 p-2">{icon}</div>
            </div>
            {detail && <p className="mt-1 text-[10px] leading-tight text-muted-foreground sm:text-xs">{detail}</p>}
            
            {previousValue !== undefined && (
              <div className="flex items-center gap-2 mt-2">
                <TrendIcon className={cn(
                  "h-3.5 w-3.5",
                  isPositive && "text-emerald-500",
                  isNegative && "text-rose-500",
                  !isPositive && !isNegative && "text-muted-foreground"
                )} />
                <p className="text-xs text-muted-foreground">
                  vs période précédente: <span className="font-medium">{previousValue}</span>
                </p>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
